import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import { useSearchParams } from "react-router-dom";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  getConnectedEdges,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import "./AutomationFlow.css";
import Start from "../../components/NodeFlow/Start/Start";
import Normal from "../../components/NodeFlow/Normal/Normal";
import Loop from "../../components/NodeFlow/Loop/Loop";
import Stop from "../../components/NodeFlow/Stop/Stop";
import Comment from "../../components/NodeFlow/Comment/Comment";
import BaseModal from "../../components/Modal/BaseModel";
import { GetBodyModalComponent } from "../../components/Modal/BodyModal/GetBodyModalComponent";
import { v4 as uuid } from "uuid";
import CustomEdge from "../../components/EdgeFlow/CustomEdge";
import {
  createScript,
  decodeScript,
  encodeScript,
  findScriptById,
  updateScript,
} from "../../request/Automation";
import {
  apiNotifications,
  systemNotifications,
} from "../../components/Notifications/Notifications";
import { ScriptContext } from "../context/ScriptContext";
import Variables from "../../components/NodeFlow/Variables/Variables";
import { DataFlowContext } from "../context/DataFlowContext";
import styled from "styled-components";
import { Group, createStyles } from "@mantine/core";
import ActionStatus from "../Navbar/actionStatus";
import { isEqual, isEmpty, cloneDeep, debounce, random } from "lodash";
import { GetDocumentModalComponent } from "../../components/Modal/BodyModal/GetDocumentModalComponent";
import ContextMenu from "./ContextMenu";
import ModalStatus from "../runOneNode/modalStatus";
import { ListProfileProvider } from "../context/ListProfilesContext";
import { AppSettingsContext } from "../context/AppSettingsContext";
import IfCode from "../../components/NodeFlow/If/IfCode";
import NodeElementExists from "../../components/NodeFlow/ElementExists";
import { FlowHandler } from "./FlowHandler";
import SearchNode from "../../components/SearchNode";

// History Stack class for Undo/Redo functionality
class HistoryStack {
  constructor(maxSize = 50) {
    this.stack = [];
    this.maxSize = maxSize;
  }

  push(state) {
    this.stack.push(state);
    if (this.stack.length > this.maxSize) {
      this.stack.shift(); // Remove oldest state when limit reached
    }
  }

  pop() {
    return this.stack.pop();
  }

  peek() {
    return this.stack[this.stack.length - 1];
  }

  isEmpty() {
    return this.stack.length === 0;
  }

  clear() {
    this.stack = [];
  }

  size() {
    return this.stack.length;
  }

  getAll() {
    return [...this.stack];
  }
}

const deboundSendScriptChangeFnc = debounce((current, nodes, edges, isInScriptWindowisInScriptWindow) => {
  const isEqualNodes = isEqual(current.nodes, nodes);
  const isEqualEdges = isEqual(current.edges, edges);
  if (!isEqualNodes || !isEqualEdges) {
    if (!isInScriptWindowisInScriptWindow) {
      window.parent.postMessage({ type: "script_change" }, "*");
    } else {
      window.electron.ipcRenderer.send("from-script-window", { type: "script_change" });
    }
  }
}, 200)

const MiniMapStyled = styled(MiniMap)`
  background-color: ${(props) => props.theme.bg};

  .react-flow__minimap-mask {
    fill: ${(props) => props.theme.minimapMaskBg};
  }

  .react-flow__minimap-node {
    fill: ${(props) => props.theme.nodeBg};
    stroke: none;
  }
`;

const ControlsStyled = styled(Controls)`
  button {
    background-color: ${(props) => props.theme.controlsBg};
    color: ${(props) => props.theme.controlsColor};
    border-bottom: 1px solid ${(props) => props.theme.controlsBorder};

    &:hover {
      background-color: ${(props) => props.theme.controlsBgHover};
    }

    path {
      fill: currentColor;
    }
  }
`;

const nodeTypes = {
  nodeStart: Start,
  normalNode: Normal,
  nodeVariables: Variables,
  nodeLoop: Loop,
  nodeStop: Stop,
  nodeComment: Comment,
  nodeIfCodeV2: IfCode,
  nodeElementExistsV2: NodeElementExists,
};

const edgeTypes = {
  customEdge: CustomEdge,
};

const useStyles = createStyles((theme) => ({
  action: {
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
    position: "absolute",
    bottom: "0",
    left: "60%",
    transform: "translate(-60%, 0)",
    padding: "30px 20px",
    height: "fit-content",
  },
}));

const defaultViewport = { x: 70, y: 150, zoom: 1.2 };


const AutomationFlow = (props, ref) => {
  const [searchParams] = useSearchParams();
  const variablesContext = useContext(DataFlowContext);
  const { classes } = useStyles();
  const initialNodes = [
    {
      id: uuid(),
      type: "nodeStart",
      height: 63,
      width: 63,
      data: { label: "Start", type: "nodeStart" },
      position: { x: 0, y: 0 },
      targetPosition: "right",
    },
    {
      id: uuid(),
      type: "nodeVariables",
      width: 120,
      height: 40,
      data: {
        label: "Variables",
        type: "nodeVariables",
        options: {
          variables: variablesContext.nodeValue.variables,
        },
      },
      position: { x: 100, y: 100 },
      targetPosition: "right",
    },
  ];
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [oneNode, setOneNode] = useNodesState(initialNodes);
  const [runOneNode, setRunOneNode] = useState(false);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nodeData, setNodeData] = useState("");
  const [initScript, setInitScript] = useState(null);
  const scriptContext = useContext(ScriptContext);
  const appSettingContext = useContext(AppSettingsContext);
  const oldState = useRef({ nodes, edges, changeCount: 0 });
  const [menu, setMenu] = useState(null);
  const refFlow = useRef(null);
  const { setViewport } = useReactFlow();

  const undoStack = useRef(new HistoryStack(50));
  const redoStack = useRef(new HistoryStack(50));
  const isUndoRedoAction = useRef(false); // Flag to prevent auto-save during undo/redo
  const isDragging = useRef(false); // Flag to track node dragging state
  const [uuidProfile, setUuidProfile] = useState({});

  const fixGroupRelationships = useCallback((nodes) => {
    const groupNodes = nodes.filter(node => node.type === 'group');

    const validNodes = nodes.map(node => {
      if (!node.parentNode) return node;

      // Tìm parent node
      const parentNode = groupNodes.find(group => group.id === node.parentNode);
      if (!parentNode) {
        return {
          ...node,
          parentNode: null,
          extent: null,
          expandParent: false
        };
      }

      const nodeWidth = node.width || 120;
      const nodeHeight = node.height || 40;
      const groupWidth = parentNode.style?.width || 300;
      const groupHeight = parentNode.style?.height || 300;

      const groupCenterX = groupWidth / 2;
      const groupCenterY = groupHeight / 2;
      const distanceFromCenter = Math.sqrt(
        Math.pow(node.position.x - groupCenterX, 2) +
        Math.pow(node.position.y - groupCenterY, 2)
      );
      const maxExpectedDistance = Math.max(groupWidth, groupHeight) * 1.5;

      const isAbsolutePosition = distanceFromCenter > maxExpectedDistance ||
                                node.position.x > groupWidth * 3 ||
                                node.position.y > groupHeight * 3 ||
                                node.position.x < -groupWidth * 2 ||
                                node.position.y < -groupHeight * 2;

      const isOutOfBounds = node.position.x < 0 ||
                           node.position.y < 0 ||
                           node.position.x + nodeWidth > groupWidth ||
                           node.position.y + nodeHeight > groupHeight;

             if (isAbsolutePosition) {

         // Convert absolute position to relative position
         let relativeX = node.position.x - parentNode.position.x;
         let relativeY = node.position.y - parentNode.position.y;

         // If the calculated relative position is still out of bounds,
         // place it in a safe location within the group
         if (relativeX < 0 || relativeY < 0 ||
             relativeX + nodeWidth > groupWidth ||
             relativeY + nodeHeight > groupHeight) {

           // Use a safe position within the group
           relativeX = Math.max(10, Math.min(50, groupWidth - nodeWidth - 10));
           relativeY = Math.max(10, Math.min(50, groupHeight - nodeHeight - 10));
         }

         return {
           ...node,
           position: {
             x: relativeX,
             y: relativeY
           },
           extent: 'parent',
           expandParent: false
         };
       } else if (isOutOfBounds) {
         // Clamp position within bounds
         const clampedX = Math.max(10, Math.min(node.position.x, groupWidth - nodeWidth - 10));
         const clampedY = Math.max(10, Math.min(node.position.y, groupHeight - nodeHeight - 10));

         return {
           ...node,
           position: {
             x: clampedX,
             y: clampedY
           },
           extent: 'parent',
           expandParent: false
         };
       }

      // Position is valid, just ensure correct properties
      return {
        ...node,
        extent: 'parent',
        expandParent: false
      };
    });

    return validNodes;
  }, []);
  const isInScriptWindow = searchParams.get('window') === "script";
  const restore = useRef([]);
  const storeFlow = useRef({ node: [], edge: [] });
  const flowHandler = useRef(new FlowHandler({setNodes, setEdges, storeFlow}));

  // Save to history (only clear future on user actions, not undo/redo)
  const saveToHistory = useCallback((clearFuture = true, source = "unknown") => {
    if (reactFlowInstance && !isUndoRedoAction.current) {
      const currentNodes = reactFlowInstance.getNodes();

      console.log('Saving to history', currentNodes);

      // Comments are already in node.data.comment, no need to sync
      const currentState = {
        nodes: currentNodes,
        edges: reactFlowInstance.getEdges(),
        viewport: reactFlowInstance.getViewport()
      };

      console.log("Current state viewport:", currentState.viewport);

      // Don't save if it's the same as the last state (strict comparison)
      const lastState = undoStack.current.peek();
      if (lastState) {
        const isNodesEqual = JSON.stringify(lastState.nodes) === JSON.stringify(currentState.nodes);
        const isEdgesEqual = JSON.stringify(lastState.edges) === JSON.stringify(currentState.edges);
        const isViewportEqual = JSON.stringify(lastState.viewport) === JSON.stringify(currentState.viewport);

        // For meaningful changes, we care about nodes and edges structure, not just viewport
        if (isNodesEqual && isEdgesEqual && isViewportEqual) {
          console.log(`Skipping duplicate state save from ${source} - no meaningful changes`);
          return;
        }

        console.log(`Saving state from ${source} - detected meaningful changes`, {
          nodesChanged: !isNodesEqual,
          edgesChanged: !isEdgesEqual,
          nodeCount: currentState.nodes.length,
          edgeCount: currentState.edges.length,
          viewport: currentState.viewport
        });
      }

      // Push current state to undo stack
      undoStack.current.push(currentState);
      // Clear redo stack if this is a new action
      if (clearFuture) {
        redoStack.current.clear();
      }
    }
  }, [reactFlowInstance, variablesContext.comment]);

  // Undo function
  const undo = useCallback(() => {

    if (undoStack.current.isEmpty()) return;

    const previous = undoStack.current.pop();

    console.log("Undo - Previous state:", previous);

    // Set flag to prevent auto-save during undo
    isUndoRedoAction.current = true;

    // Save current state to redo stack
    if (reactFlowInstance) {
      const currentState = {
        nodes: reactFlowInstance.getNodes(),
        edges: reactFlowInstance.getEdges(),
        viewport: reactFlowInstance.getViewport()
      };

      redoStack.current.push(currentState);

      // Restore previous state
      const { nodes: prevNodes, edges: prevEdges, viewport: prevViewport } = previous;

      console.log("Previous state viewport:", previous.viewport);

      // Fix group relationships trước khi set nodes
      const fixedNodes = fixGroupRelationships(prevNodes || []);
      setNodes(fixedNodes);
      setEdges(prevEdges || []);
      if (prevViewport && reactFlowInstance) {
        console.log('Using reactFlowInstance.setViewport for undo:', prevViewport);
        reactFlowInstance.setViewport(prevViewport);
      }
      console.log("Undo stack:", undoStack.current.getAll());
      console.log("Redo stack:", redoStack.current.getAll());
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  }, [reactFlowInstance, setNodes, setEdges, setViewport, variablesContext]);

  // Redo function
  const redo = useCallback(() => {

    if (redoStack.current.isEmpty()) {
      console.log("No future states to redo");
      return;
    }

    const next = redoStack.current.pop();

    console.log("Redo - Next state:", { nodes: next.nodes?.length, edges: next.edges?.length });

    // Set flag to prevent auto-save during redo
    isUndoRedoAction.current = true;

    // Save current state to undo stack
    if (reactFlowInstance) {
      const currentState = {
        nodes: reactFlowInstance.getNodes(),
        edges: reactFlowInstance.getEdges(),
        viewport: reactFlowInstance.getViewport()
      };

      undoStack.current.push(currentState);

      // Restore next state
      const { nodes: nextNodes, edges: nextEdges, viewport: nextViewport } = next;

      // Fix group relationships trước khi set nodes
      const fixedNodes = fixGroupRelationships(nextNodes || []);
      setNodes(fixedNodes);
      setEdges(nextEdges || []);

      // Comments are already in node.data.comment, no need to extract

      if (nextViewport && reactFlowInstance) {
        console.log('Using reactFlowInstance.setViewport for redo:', nextViewport);
        reactFlowInstance.setViewport(nextViewport);
      }

      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  }, [reactFlowInstance, setNodes, setEdges, setViewport, variablesContext]);

  const onConnect = useCallback(
    (params) => {
      // Save current state before connecting (clear future since this is a new user action)
      saveToHistory(true, "onConnect");

      let colorEdges = {
        stroke: "var(--output)",
      };
      if (params.sourceHandle.includes("error")) {
        colorEdges.stroke = "var(--output-bottom)";
      }
      setEdges((eds) => {
        return addEdge(
          {
            ...params,
            type: "customEdge",
            style: colorEdges,
            data: { setEdges },
          },
          eds
        )
      }

      );
    },
    [edges, setEdges, saveToHistory]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Handle node drag start - record starting position and save state
  const onNodeDragStart = useCallback((event, node) => {
    isDragging.current = true;

    // Save the starting position to compare later
    if (reactFlowInstance) {
      const currentNode = reactFlowInstance.getNode(node.id);
      dragStartPosition.current = {
        ...currentNode.position,
        hasSavedBeforeState: false // Track if we've saved the "before" state
      };

      // Don't save state immediately - wait to see if there's actual movement
    }
  }, [reactFlowInstance]);

  // Handle viewport change (zoom/pan) - save after 500ms of no movement
  const viewportChangeTimeout = useRef(null);
  const lastViewport = useRef(null);

  const onMoveEnd = useCallback((event, viewport) => {
    // Clear previous timeout
    if (viewportChangeTimeout.current) {
      clearTimeout(viewportChangeTimeout.current);
    }

    // Save viewport after 500ms of no movement (debounce)
    viewportChangeTimeout.current = setTimeout(() => {
      if (reactFlowInstance && !isUndoRedoAction.current) {
        const currentViewport = reactFlowInstance.getViewport();

        // Check if viewport actually changed
        if (lastViewport.current) {
          const changed = JSON.stringify(lastViewport.current) !== JSON.stringify(currentViewport);
          if (!changed) {
            console.log('Viewport unchanged, skipping save');
            return;
          }
        }

        console.log('Viewport changed after 500ms, saving to history:', currentViewport);
        lastViewport.current = currentViewport;
        saveToHistory(true, "viewportChange");
      }
    }, 500);
  }, [reactFlowInstance, saveToHistory]);

  // Handle node drag stop - only save if position actually changed
  const dragStartPosition = useRef(null);

  const onNodeDragStop = useCallback((event, node) => {
    isDragging.current = false;

    // Check if position actually changed
    if (dragStartPosition.current && reactFlowInstance && !isUndoRedoAction.current) {
      const currentNode = reactFlowInstance.getNode(node.id);
      const startPos = dragStartPosition.current;
      const currentPos = currentNode.position;

      // Only save if position significantly changed (threshold to avoid micro-movements)
      const threshold = 1; // pixels
      const moved = Math.abs(currentPos.x - startPos.x) > threshold ||
                   Math.abs(currentPos.y - startPos.y) > threshold;

      if (moved) {

        // Save the "before" state if we haven't already
        if (!startPos.hasSavedBeforeState) {
          // Create a temporary state with the original position and comments
          const nodesWithOriginalPosition = reactFlowInstance.getNodes().map(n => {
            if (n.id === node.id) {
              const nodeWithOriginalPos = { ...n, position: { x: startPos.x, y: startPos.y } };
              // Add comment to node data if exists
              if (variablesContext.comment[n.id]) {
                nodeWithOriginalPos.data = {
                  ...nodeWithOriginalPos.data,
                  comment: variablesContext.comment[n.id]
                };
              }
              return nodeWithOriginalPos;
            } else {
              // Add comment to other nodes if exists
              const nodeWithComment = { ...n };
              if (variablesContext.comment[n.id]) {
                nodeWithComment.data = {
                  ...nodeWithComment.data,
                  comment: variablesContext.comment[n.id]
                };
              }
              return nodeWithComment;
            }
          });

          const beforeState = {
            nodes: nodesWithOriginalPosition,
            edges: reactFlowInstance.getEdges(),
            viewport: reactFlowInstance.getViewport()
          };

          // Manually add to history using stack
          undoStack.current.push(beforeState);
          redoStack.current.clear();
        }
      } else {
        console.log("Node position unchanged, not saving state");
      }
    }

    dragStartPosition.current = null;
  }, [reactFlowInstance, variablesContext.comment]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");
      const data = JSON.parse(event.dataTransfer.getData("data"));
      if (typeof type === "undefined" || !type) {
        return;
      }

      // Save current state before adding new node (clear future since this is a new user action)
      saveToHistory(true, "onDrop");

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: uuid(),
        type: type,
        position,
        data: {
          ...data,
          onRemoveNode: onRemoveNode,
          onUpdateNode: onUpdateNode,
          onStartOneNode: onStartOneNode,
        },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, saveToHistory]
  );

  const onRemoveNode = (nodeId) => {
    // Save current state before removing (clear future since this is a new user action)
    saveToHistory(true, "onRemoveNode");

    const edges = reactFlowInstance.getEdges();
    const edgesToRemove = getConnectedEdges(
      [reactFlowInstance.getNode(nodeId)],
      edges
    );

    //xóa tất cả edge nối tới node
    if (edgesToRemove.length) {
      reactFlowInstance.setEdges((edges) =>
        edges.filter(
          (ed) =>
            !edgesToRemove.find((edgeToRemove) => edgeToRemove.id === ed.id)
        )
      );
    }

    //xóa node
    reactFlowInstance.setNodes((nds) =>
      nds.filter((node) => node.id !== nodeId)
    );
  };

  const onStartOneNode = (nodeId) => {
    const scripts = reactFlowInstance.toObject();
    const node = scripts.nodes.find((n) => n.id === nodeId);
    if (appSettingContext.browserRunTest.current) {
      window.api.mb_ipcRenderer.sendMsg("automation_flow", {
        action: "runOneNode",
        browser_name: appSettingContext.browserRunTest.current.name,
        browser_uuid: appSettingContext.browserRunTest.current.uuid,
        scripts: JSON.stringify(scripts),
        oneNode: JSON.stringify(node),
      });
      props.handleShowLogAutomationFlow();
    } else {
      setOneNode(node);
      setRunOneNode(true);
    }
  };

  const onUpdateNode = (nodeId) => {
    const node = reactFlowInstance
      .toObject()
      .nodes.find((n) => n.id === nodeId);
    const NODE_NOT_OPEN_MODAL = ["stopListenRequest", "stopCode", "stopLoop"];
    if (NODE_NOT_OPEN_MODAL.includes(node.data.type)) return;
    setNodeData(node);
    setShowModal(true);
  };

  const onClickNode = (e, node) => {
    if (node.type === "group") {
      return;
    }
    const NODE_NOT_OPEN_MODAL = ["stopListenRequest", "stopCode", "stopLoop"];
    if (NODE_NOT_OPEN_MODAL.includes(node.data.type)) return;
    setShowModal(true);
    setNodeData(node);
  };

  const updateDataInNode = (data) => {
    // Save current state before updating (clear future since this is a new user action)
    saveToHistory(true, "updateDataInNode");

    setNodes((nds) =>
      nds.map((node) => {
        if (data && node.id === nodeData.id) {
          node.data.options = { ...data };
          if (nodeData.type === "nodeIfCodeV2" || nodeData.type === "nodeElementExistsV2") { // === if V2
            const newUuid = uuid();
            const oldUuid = node.id
            node.id = newUuid
            replaceAllUuidInEdge({ oldUuid, newUuid })
          }
        }
        return node;
      })
    );
  };

  const replaceAllUuidInEdge = ({ oldUuid, newUuid }) => {
    const edges = reactFlowInstance.getEdges();
    const newEdges = cloneDeep(edges)
    newEdges.forEach(eds => {
      if (eds.target === oldUuid || eds.source === oldUuid) {
        for (const key in eds) {
          if (typeof eds[key] === "string") {
            eds[key] = eds[key].replaceAll(oldUuid, newUuid);
          }
        }
      }
    })
    setEdges(newEdges)
  }
  useImperativeHandle(ref, () => ({
    handleDbClick(newNode) {
      const randomNumber = Math.floor(Math.random() * 100) + 50;
      const { width, height } =
        reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: width / 2 + randomNumber,
        y: height / 2 + randomNumber,
      });
      newNode.data.onRemoveNode = onRemoveNode;
      newNode.data.onUpdateNode = onUpdateNode;
      setNodes((nds) => nds.concat({ ...newNode, position: position }));
    },
    handleSave: async function (script, saveAs = false) {
      if (reactFlowInstance) {
        const flow = reactFlowInstance.toObject();
        // Save variables
        const variableNode = flow.nodes.find(item => item.type === "nodeVariables" && !!item?.data?.options?.variables);
        variableNode.data.options.variables = cloneDeep(variablesContext.nodeValue.variables);

        // Save comments for each node
        flow.nodes.forEach(node => {
          if (variablesContext.comment[node.id]) {
            node.data.comment = variablesContext.comment[node.id];
          }
        });

        const flow_encoded = await encodeScript(JSON.stringify(flow))
        const idNodeStart = flow.nodes.find(n => n.type === "nodeStart").id
        // Check nối start đến node
        const hasEdgeStart = flow.edges.some(i => i.source === idNodeStart)
        if (!flow.edges.length || !hasEdgeStart) {
          systemNotifications({
            type: "error",
            message: "Please define start command of the scenario!",
          });
          return { type: "error" };
        }

        const allOtherScript = flow.nodes.filter(
          (node) =>
            ["normalNode"].includes(node.type) &&
            node.data &&
            ["runOtherScript"].includes(node.data.type)
        );
        const allCustomNode = flow.nodes.filter(
          (node) =>
            ["normalNode"].includes(node.type) &&
            node.data &&
            ["customNode"].includes(node.data.type)
        );
        const key = allOtherScript.map((item) => item.data.options.script_key);
        const key_nodes = allCustomNode.map((item) => item.data.options.key);
        const body = {
          name: script?.name ?? "flow",
          type: "flow",
          status: "active",
          password: script.usingPassword ? script.password : null,
          list_children: Array.from(new Set(key)),
          list_key_node: Array.from(new Set(key_nodes)),
          script: flow_encoded,
          folder_script_id: script?.folder_script_id ?? null
        };
        if (scriptContext.scriptId && saveAs === false) {
          if (script.usingPassword && script.password === "") delete body.password
          updateScript({ body: body, id: scriptContext.scriptId })
            .then((res) => {
              apiNotifications(res);
              scriptContext.setScriptPassword(script?.usingPassword)
              if (!isInScriptWindow) {
                window.parent.postMessage({ type: "script_saved" }, "*");
              } else {
                window.electron.ipcRenderer.send("from-script-window", { type: "script_saved" });
              }
              window.electronAPI.saveScript({
                ...body,
                key: scriptContext.scriptId,
              });
            })
            .catch((err) => {
              apiNotifications(err);
            });
          return;
        }

        createScript({ body: body })
          .then((res) => {
            apiNotifications(res);
            scriptContext.setScriptPassword(script?.usingPassword)
            scriptContext.setScriptId(res.data.content.id);
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('updateId', res.data.content.id);
            window.history.replaceState(null, '', currentUrl.toString());
            if (!isInScriptWindow) {
              window.parent.postMessage({ type: "script_saved" }, "*");
            } else {
              window.electron.ipcRenderer.send("from-script-window", { type: "script_saved" });
            }
            window.electronAPI.saveScript({
              ...body,
              password: script.password,
              key: res.data.content.id,
            });
          })
          .catch((err) => {
            apiNotifications(err);
          });
      }
    },
    handleRunTest(uuid, name) {
      appSettingContext.browserRunTest.current = { uuid, name };
      const variables = reactFlowInstance.toObject().nodes.find(item => item.type == "nodeVariables").data.options.variables
      window.api.mb_ipcRenderer.sendMsg("variables", variables)
      window.api.mb_ipcRenderer.sendMsg("automation_flow", {
        action: runOneNode ? "runOneNode" : "run",
        browser_name: name,
        browser_uuid: uuid,
        scripts: JSON.stringify(reactFlowInstance.toObject()),
        oneNode: runOneNode ? JSON.stringify(oneNode) : {},
      });
    },
    handleShowLog() {
      window.api.mb_ipcRenderer.sendMsg("automation_flow", {
        action: "show_log",
      });
    },
    handleRemoveNodes() {
      const nodes = reactFlowInstance.getNodes();
      const nodesDefault = ["nodeStart", "nodeVariables"];
      const nodesToRemove = nodes
        .filter((node) => node.selected && !nodesDefault.includes(node.type))
        .map((n) => n.id);
      // xóa tất cả edge nối tới nodes
      reactFlowInstance.setEdges((edges) =>
        edges.filter(
          (edge) =>
            !nodesToRemove.includes(edge.target) &&
            !nodesToRemove.includes(edge.source)
        )
      );

      // xoá nodes
      reactFlowInstance.setNodes((nds) =>
        nds.filter((node) => !nodesToRemove.includes(node.id))
      );
    },
    handleShowVariables() {
      setShowModal(true);
      const nodes = reactFlowInstance.getNodes();
      const nodeVariables = nodes.find((nds) => nds.type === "nodeVariables");
      setNodeData(nodeVariables);
    },
    handleCustomNode(data) {
      const nodes = cloneDeep(reactFlowInstance.getNodes());
      const newNodes = data?.map(i => ({
        ...i,
        id: uuid(),
        type: "normalNode"
      }))
      const randomNumber = Math.floor(Math.random() * 100) + 50;
      const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
      let position = reactFlowInstance.project({
        x: width / 2 + randomNumber,
        y: height / 2 + randomNumber,
      });
      newNodes.forEach(ele => {
        ele.data.onRemoveNode = onRemoveNode
        ele.data.onUpdateNode = onUpdateNode
        ele.data.onStartOneNode = onStartOneNode
        ele.position = position
        position = {
          x: position.x + 20,
          y: position.y + 55
        }
      });
      setNodes([...nodes, ...newNodes]);
    },
    addScript({ nodes, edges }) {
      if (nodes) {
        for (const node of nodes) {
          node.data.onRemoveNode = onRemoveNode;
          node.data.onUpdateNode = onUpdateNode;
          node.data.onStartOneNode = onStartOneNode;
        }
        setNodes((nds) => nds.concat(nodes))
      }
      if (edges) {
        for (const edge of edges) {
          edge.data = { setEdges }
        }
        setEdges((eds) => eds.concat(edges))
      }
    },
    getPosition() {
      const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: width / 2,
        y: height / 2,
      });
      return position
    }
  }));

  useEffect(() => {
    if (props.updateId) {
      if (initScript === null) {
        findScriptById({ id: props.updateId })
          .then(async (res) => {
            const scriptDecoded = await decodeScript(res.data.content?.script)
            setInitScript(JSON.parse(scriptDecoded));
            document.title = res.data.content?.name + " - Hidemium Automation"
            scriptContext.setScriptId(res.data.content.key);
            scriptContext.setScriptName(res.data.content.name);
            scriptContext.setScriptPassword(res.data.content.isset_password);
            scriptContext.set_folder_script_id(res.data.content.folder_script_id);
          })
          .catch((err) => {
            console.log({ err });
            apiNotifications(err);
          });
      } else {
        let scriptContent = initScript;
        let commentData = {};
        scriptContent.nodes.forEach((element) => {
          if (element.type === "group") {
            return element;
          }
          if (element.data) {
            element.data = Object.assign(element.data, {
              onRemoveNode: onRemoveNode,
              onUpdateNode: onUpdateNode,
              onStartOneNode: onStartOneNode,
            });
          }
          // Restore comment from node data
          if (element.data.comment) {
            commentData[element.id] = element.data.comment;
          }
          // Also check nodeComment type for backward compatibility
          if (element.type === "nodeComment") {
            if (element.data.options.hasOwnProperty("id")) {
              commentData[element.data.options.id] = element.data.options.value;
            }
          }
        });
        variablesContext.setComment(commentData);
        setNodes(scriptContent.nodes);
        const variableNode = scriptContent.nodes.find(item => item.type === "nodeVariables" && !!item?.data?.options?.variables);
        variablesContext.setNodeValue(variableNode.data.options);
        scriptContent.edges.map((element) => {
          setEdges((eds) =>
            addEdge({ ...element, type: "customEdge", data: { setEdges } }, eds)
          );
        });
        setEdges((eds) => {
          oldState.current.edges = eds;
          return eds;
        });
        oldState.current.nodes = scriptContent.nodes;
      }
    }
  }, [props.updateId, setNodes, setEdges, initScript]);

  const compareScriptAndSendToParent = () => {
    deboundSendScriptChangeFnc(oldState.current, nodes, nodes, isInScriptWindow)
  };

  useEffect((...args) => {
    if (oldState.current.changeCount <= 4) {
      oldState.current.changeCount = oldState.current.changeCount + 1;
    }
    if (!props.updateId && oldState.current.changeCount > 2) {
      compareScriptAndSendToParent();
    } else if (props.updateId && oldState.current.changeCount > 4) {
      compareScriptAndSendToParent();
    }
  }, [nodes, edges, props.updateId]);

  const onSelectionContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      const pane = refFlow.current.getBoundingClientRect();
      setMenu({
        top: event.clientY < pane.height - 100 && event.clientY,
        left: event.clientX - 360 < pane.width - 100 && event.clientX - 360,
        right:
          event.clientX - 360 >= pane.width - 100 &&
          pane.width - (event.clientX - 360),
        bottom:
          event.clientY >= pane.height - 100 && pane.height - event.clientY,
      });
    },
    [setMenu]
  );

  const onPaneContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      const pane = refFlow.current.getBoundingClientRect();
      setMenu({
        onPane: true,
        top: event.clientY < pane.height - 100 && event.clientY,
        left: event.clientX - 360 < pane.width - 100 && event.clientX - 360,
        right:
          event.clientX - 360 >= pane.width - 100 &&
          pane.width - (event.clientX - 360),
        bottom:
          event.clientY >= pane.height - 100 && pane.height - event.clientY,
      });
    },
    [setMenu]
  );

  const onNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      const nodesDefault = ["nodeStart", "nodeVariables", "nodeComment"];
      if (nodesDefault.includes(node.type)) return;
      const pane = refFlow.current.getBoundingClientRect();
      setMenu({
        id: node.id,
        top: event.clientY < pane.height - 100 && event.clientY,
        left: event.clientX - 360 < pane.width - 100 && event.clientX - 360,
        right:
          event.clientX - 360 >= pane.width - 100 &&
          pane.width - (event.clientX - 360),
        bottom:
          event.clientY >= pane.height - 100 && pane.height - event.clientY,
      });
    },
    [setMenu]
  );

  const onPaneClick = useCallback(() => setMenu(null), [setMenu]);

  const duplicateNode = useCallback(
    (id) => {
      // Save current state before duplicating (clear future since this is a new user action)
      saveToHistory(true, "duplicateNode");

      const nodesDefault = ["nodeStart", "nodeVariables", "nodeComment", "group"];
      if (id) {
        const node = reactFlowInstance.getNode(id);
        if (nodesDefault.includes(node.type)) {
          return;
        }
        const cloneNode = cloneDeep(node);
        cloneNode.parentNode = null;
        cloneNode.extent = null;
        for (const node of nodes) {
          node.selected = false;
        }
        setNodes(nodes);
        const position = {
          x: node.position.x + 50,
          y: node.position.y + 50,
        };
        setNodes((nds) =>
          nds.concat({ ...cloneNode, id: uuid(), position, selected: true })
        );
      } else {
        const selectedNodes = nodes.filter(
          (nds) => nds.selected && !nodesDefault.includes(nds.type)
        );
        const cloneNodes = cloneDeep(selectedNodes);
        const selectedEdges = edges.filter((eds) => eds.selected);
        const uuidMapping = {};
        const nodeToDuplicate = cloneNodes
          .filter((node) => !nodesDefault.includes(node.type))
          .map((node) => {
            const newUuid = uuid();
            uuidMapping[node.id] = newUuid;
            return {
              ...node,
              parentNode: null,
              extent: null,
              id: newUuid,
              position: {
                x: node.position.x + 50,
                y: node.position.y + 50,
              },
            };
          });
        const newSelectedEdges = selectedEdges.filter(
          (eds) =>
            selectedNodes.some(
              (nds) => nds.id === eds.source && !nodesDefault.includes(nds.type)
            ) &&
            selectedNodes.some(
              (nds) => nds.id === eds.target && !nodesDefault.includes(nds.type)
            )
        );
        const edgeToDuplicate = newSelectedEdges.map((edge) => ({
          source: uuidMapping[edge.source]
            ? uuidMapping[edge.source]
            : edge.source,
          target: uuidMapping[edge.target]
            ? uuidMapping[edge.target]
            : edge.target,
          selected: true,
          targetHandle: edge.targetHandle.replace(
            edge.target,
            uuidMapping[edge.target]
          ),
          sourceHandle: edge.sourceHandle.replace(
            edge.source,
            uuidMapping[edge.source]
          ),
          type: "customEdge",
          id: `reactflow__edge-${uuid()}`,
          data: { setEdges },
        }));
        for (const edge of selectedEdges) {
          edge.selected = false;
        }
        for (const node of selectedNodes) {
          node.selected = false;
        }
        const nodeRemains = nodes
          .filter((n) => !selectedNodes.some((on) => n.id === on.id))
          .map((item) => ({ ...item, selected: false }));
        const newNodes = [...nodeRemains, ...selectedNodes, ...nodeToDuplicate];
        setNodes(newNodes);
        const edgeRemains = edges
          .filter((fil) => !selectedEdges.some((x) => x.id === fil.id))
          .map((item) => ({ ...item, selected: false }));
        setEdges([...edgeRemains, ...selectedEdges, ...edgeToDuplicate]);
      }
    },
    [nodes, edges, saveToHistory]
  );

  const deleteNode = useCallback((id) => {
    const nodesDefault = ["nodeStart", "nodeVariables"];
    let removeNodes = [];
    let removeNodeIds = [];
    let removeGroups = [];
    let removeGroupIds = [];
    if (id) {
      removeNodes.push(nodes.find(n => n.id === id));
    } else {
      removeNodes = nodes.filter((nds) => nds.selected && !nodesDefault.includes(nds.type));
    }

    if (isEmpty(removeNodes)) {
      return;
    }

    // Save current state before deleting (clear future since this is a new user action)
    saveToHistory(true, "deleteNode");

    for (const removeNode of removeNodes) {
      removeNodeIds.push(removeNode.id);
      if (removeNode.type === 'group') {
        removeGroupIds.push(removeNode.id);
        removeGroups.push(removeNode);
      }
    }

    // Create new array without removed nodes, cloning to avoid mutation
    let remainNodes = nodes.filter(n => !removeNodeIds.includes(n.id)).map(node => ({ ...node }));

    // Handle child nodes of removed groups - convert to absolute position
    if (!isEmpty(removeGroups)) {
      remainNodes = remainNodes.map(item => {
        if (removeGroupIds.includes(item.parentNode)) {
          const parent = removeGroups.find(g => g.id === item.parentNode);

          return {
            ...item,
            parentNode: null,
            extent: null,
            expandParent: false,
            position: {
              x: item.position.x + (parent?.position.x || 0),
              y: item.position.y + (parent?.position.y || 0)
            }
          };
        }
        return item;
      });
    }

    setNodes(remainNodes);
    setEdges((eds) => {
      return eds.filter((edge) => {
        return !removeNodeIds.includes(edge.source) && !removeNodeIds.includes(edge.target);
      })
    });

    // Comments are in node.data.comment, will be deleted automatically with node
  }, [nodes, saveToHistory]);

  const groupNode = useCallback(() => {
    // Save current state before grouping (clear future since this is a new user action)
    saveToHistory(true, "groupNode");
    flowHandler.current.handleGroup(nodes);
  }, [nodes, saveToHistory]);

  const ungroupNode = useCallback((id) => {
    // Save current state before ungrouping (clear future since this is a new user action)
    saveToHistory(true, "ungroupNode");
    flowHandler.current.handleUngroup(nodes, id);
  }, [nodes, saveToHistory]);

  const copyNode = useCallback(() => {
    flowHandler.current.handleCopy({ edges, nodes, variablesContext });
  }, [nodes, edges, variablesContext.comment]);

  const isHasNode = (nodes, nodeToCheck) => {
    return nodes.some((n) => n.id === nodeToCheck.id);
  };

  const pasteNode = useCallback(
    (pasteNode, event) => {
      const storageScript = localStorage.getItem('NodeDraft')
      let selectedNodes = cloneDeep(storeFlow.current.node);
      let cloneEdges = cloneDeep(storeFlow.current.edge);
      if (storageScript) {
        const jsonParseScript = JSON.parse(storageScript)
        selectedNodes = cloneDeep(jsonParseScript?.node)
        selectedNodes.forEach(element => {
          if (element.data) {
            element.data.onRemoveNode = onRemoveNode;
            element.data.onUpdateNode = onUpdateNode;
            element.data.onStartOneNode = onStartOneNode;
          }
        });
        cloneEdges = cloneDeep(jsonParseScript?.edge)
      }
      if (isEmpty(selectedNodes)) return;

      // Save current state before pasting (clear future since this is a new user action)
      saveToHistory(true, "pasteNode");
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const oldEdges = cloneDeep(edges);
      const newSelectedEdges = cloneEdges.filter(
        (eds) =>
          selectedNodes.some((nds) => nds.id === eds.source) &&
          selectedNodes.some((nds) => nds.id === eds.target)
      );
      let newNode = [];
      const uuidMapping = {};
      const { left, top } = reactFlowBounds;
      if (pasteNode && storeFlow.current.node.length && event) {
        const { clientX: mouseX, clientY: mouseY } = event;
        const firstNodeToPaste = storeFlow.current.node.reduce(
          (previous, current) => {
            return current.position.y < previous.position.y
              ? current
              : previous;
          }
        );
        const positionFNodeX = parseInt(firstNodeToPaste.position.x);
        const positionFNodeY = parseInt(firstNodeToPaste.position.y);
        const zoom = reactFlowInstance.getZoom();
        newNode = selectedNodes.map((nds) => {
          const newUuid = uuid();
          uuidMapping[nds.id] = newUuid;
          return {
            ...nds,
            id: newUuid,
            data: cloneDeep(nds.data),
            position: reactFlowInstance.project({
              x: mouseX + (nds.position.x - positionFNodeX) * zoom - left,
              y: mouseY + (nds.position.y - positionFNodeY) * zoom - top,
            }),
            selected: true,
          };
        });
      } else {
        newNode = selectedNodes.map((nds) => {
          const newUuid = uuid();
          uuidMapping[nds.id] = newUuid;
          return {
            ...nds,
            parentNode: null,
            extent: null,
            data: cloneDeep(nds.data),
            id: newUuid,
            position: {
              x: nds.position.x + 50,
              y: nds.position.y + 50,
            },
            selected: true,
          };
        });
      }
      const edgeToPaste = newSelectedEdges.map((edge) => {
        return {
          source: uuidMapping[edge.source]
            ? uuidMapping[edge.source]
            : edge.source,
          target: uuidMapping[edge.target]
            ? uuidMapping[edge.target]
            : edge.target,
          selected: true,
          targetHandle: edge.targetHandle.replace(
            edge.target,
            uuidMapping[edge.target]
          ),
          sourceHandle: edge.sourceHandle.replace(
            edge.source,
            uuidMapping[edge.source]
          ),
          type: "customEdge",
          id: `reactflow__edge-${uuid()}`,
          data: { setEdges },
        };
      });
      for (const node of selectedNodes) {
        node.selected = false;
      }
      for (const edge of oldEdges) {
        edge.selected = false;
      }

      const edgeRemains = edges
        .filter((fil) => !oldEdges.some((x) => x.id === fil.id))
        .map((item) => ({ ...item, selected: false }));
      const nodeRemains = nodes
        .filter((n) => !selectedNodes.some((on) => n.id === on.id))
        .map((item) => ({ ...item, selected: false }));
      const newSelectedNode = selectedNodes.filter((node) =>
        isHasNode(nodes, node)
      );
      setNodes([...nodeRemains, ...newNode, ...newSelectedNode]);
      setEdges([...edgeRemains, ...oldEdges, ...edgeToPaste]);
    },
    [nodes, saveToHistory]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showModal) return;
      const { value } = event.target;
      const code = event.which || event.keyCode;
      let charCode = String.fromCharCode(code).toLowerCase();
      const selectNodes = nodes.filter((nds) => nds.selected);

      if ((event.ctrlKey || event.metaKey) && event.shiftKey && (charCode === "z" || event.key === "Z")) {
        event.preventDefault();
        redo();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && (charCode === "z" || event.key === "z")) {
        event.preventDefault();
        undo();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && charCode === "d") {
        if (!isEmpty(selectNodes)) {
          duplicateNode();
        }
      } else if (
        (event.key === "Backspace" || event.key === "Delete") &&
        value === undefined
      ) {
        if (!isEmpty(selectNodes)) {
          deleteNode();
        }
      } else if ((event.ctrlKey || event.metaKey) && charCode === "c") {
        copyNode();
      } else if ((event.ctrlKey || event.metaKey) && charCode === "v") {
        pasteNode();
      } else if ((event.ctrlKey || event.metaKey) && charCode === "g") {
        groupNode();
      } else if ((event.ctrlKey || event.metaKey) && charCode === "s") {
        event.preventDefault();
        saveToHistory(false, "manual_save"); // Don't clear future on manual save
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nodes, showModal, undo, redo, saveToHistory]);

  // Save initial state when ReactFlow is initialized (with delay to ensure stability)
  useEffect(() => {
    if (reactFlowInstance && undoStack.current.isEmpty()) {
      const timeoutId = setTimeout(() => {
        if (undoStack.current.isEmpty()) { // Double-check no state was saved yet
          console.log("Saving initial state");
          saveToHistory(false, "initial_state"); // Don't clear future when saving initial state
        } else {
          console.log("Initial state already saved, skipping");
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [reactFlowInstance, saveToHistory]);

  const handleStart = () => {
    if (!uuidProfile.uuid) {
      handleError({ profile: "Choose a profile to run on" });
      return;
    } else {
      setRunOneNode(false);
    }
    props.handleRunAutomationFlow(uuidProfile.uuid, uuidProfile.browser_name);
    props.handleShowLogAutomationFlow();
  };
  const handleError = (errors) => {
    if (errors.name) {
      systemNotifications({
        type: "error",
        color: "red",
        message: errors.name,
      });
    }
    if (errors.profile) {
      systemNotifications({
        type: "error",
        color: "red",
        message: errors.profile,
      });
    }
  };

  useEffect(() => {
    if (!window.electron?.ipcRenderer?.removeAllListeners) return;
    window.electron.ipcRenderer.removeAllListeners("active-node");
    window.electron.ipcRenderer.on("active-node", (_, data) => {
      appSettingContext.setNodeActive(data)
    });
    return () => {
      window.electron.ipcRenderer.removeAllListeners("active-node");
    };
  }, [nodes]);

  return (
    <>
      <SearchNode reactFlowInstance={reactFlowInstance}/>
      <div className="content">
        <div className="dndflow">
          <ReactFlowProvider>
            <div className="reactflow-wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                ref={refFlow}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                onMoveEnd={onMoveEnd}
                // fitView
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodeDoubleClick={onClickNode}
                defaultViewport={defaultViewport}
                deleteKeyCode={null}
                onPaneClick={onPaneClick}
                onNodeContextMenu={onNodeContextMenu}
                onSelectionContextMenu={onSelectionContextMenu}
                onPaneContextMenu={onPaneContextMenu}
              >
                <Background color="#99b3ec" variant={"dots"} />
                <ControlsStyled />
                <MiniMapStyled nodeStrokeWidth={3} zoomable pannable />
                {menu && (
                  <ContextMenu
                    onClick={onPaneClick}
                    deleteNode={deleteNode}
                    duplicateNode={duplicateNode}
                    copyNode={copyNode}
                    pasteNode={pasteNode}
                    groupNode={groupNode}
                    ungroupNode={ungroupNode}
                    {...menu}
                  />
                )}
              </ReactFlow>
            </div>
          </ReactFlowProvider>
          <div className={classes.action}>
            <Group
              position="center"
              spacing="sm"
              style={{ flexWrap: "nowrap" }}
            >
              <ActionStatus
                props={props}
                nodes={nodes}
                setRunOneNode={setRunOneNode}
              />
            </Group>
          </div>
        </div>
        {showModal && (
          <BaseModal
            show={showModal}
            setShowModal={setShowModal}
            bodyModal={GetBodyModalComponent[nodeData?.data?.type]}
            documentModal={GetDocumentModalComponent[nodeData?.data?.type]}
            nodeData={nodeData}
            updateDataInNode={updateDataInNode}
            setEdges={setEdges}
          />
        )}
        {runOneNode && (
          <ListProfileProvider>
            <ModalStatus
              show={runOneNode}
              setShowModal={setRunOneNode}
              setUuidProfile={setUuidProfile}
              handleStart={handleStart}
            />
          </ListProfileProvider>
        )}
      </div>
    </>
  );
};

export default forwardRef(AutomationFlow);
